/**
 * Neuigkeiten — der einmalige Dialog nach dem Anmelden (HM-170, ADR 0048).
 *
 * Aufbau nach `ui_kits/dashboard/Neuigkeiten.html`: breit das GIF links und
 * rechts alle Funktionen der Fassung untereinander, die gewählte klappt ihren
 * Text auf; schmal (unter `breakpoints.shell`) das GIF oben, die Liste darunter,
 * die Knöpfe teilen sich die untere Zeile.
 *
 * **Gesehen ist er nach „Fertig", nach dem Schließen und nach dem Weg zur
 * Funktion** — in allen drei Fällen hat jemand ihn wahrgenommen. Gespeichert
 * wird am Konto (`PATCH /auth/me` mit `newsSeen`); schlägt das fehl, schließt er
 * trotzdem und kommt beim nächsten Anmelden wieder. Ein Dialog, der sich nicht
 * schließen lässt, weil ein Netz hakt, wäre der schlechtere Fehler.
 *
 * Ein eigenes `<dialog>` statt des `Dialog` aus dem Design-System: Dieser hat
 * Kopf, Inhalt und Fußleiste übereinander, der Entwurf eine zweigeteilte Fläche
 * mit dem GIF auf voller Höhe. Fokusfalle und Escape kommen wie dort von
 * `showModal`.
 */
import { Button, breakpoints, icons, useNarrow } from "@hausmeter/ui";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { api } from "../lib/client.js";
import type { NewsEntry, NewsTarget } from "../lib/news.js";
import { replaceUser } from "../lib/session.js";

export interface NewsDialogProps {
  readonly entry: NewsEntry;
  /** Nach dem Schließen, gleich wie — der Rahmen blendet den Dialog aus. */
  readonly onDone: () => void;
  /** Der Weg zur Funktion eines Eintrags. */
  readonly onOpen: (target: NewsTarget) => void;
}

export function NewsDialog({ entry, onDone, onOpen }: NewsDialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const narrow = useNarrow(breakpoints.shell);
  const [index, setIndex] = useState(0);
  const items = entry.items;
  const item = items[index] ?? items[0]!;
  const last = index >= items.length - 1;

  useEffect(() => {
    const element = ref.current;
    if (element !== null && !element.open) element.showModal();
  }, []);

  function finish(then?: NewsTarget): void {
    ref.current?.close();
    // Gesehen ist gesehen — auch wenn das Speichern scheitert, bleibt er zu.
    void api
      .PATCH("/auth/me", { body: { newsSeen: entry.id } })
      .then(({ data }) => {
        if (data !== undefined) replaceUser(data);
      })
      .catch(() => undefined);
    onDone();
    if (then !== undefined) onOpen(then);
  }

  const heading = (
    <span id="news-title" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: "var(--hm-green-700)" }}>
      <icons.IconSparkle size={16} />
      Neu in HausMeter <span style={{ fontFamily: "var(--font-mono)", fontWeight: 500 }}>{entry.version}</span>
    </span>
  );

  const close = (
    <button
      type="button"
      aria-label="Schließen"
      onClick={() => finish()}
      style={{
        display: "inline-flex",
        width: 32,
        height: 32,
        flexShrink: 0,
        alignItems: "center",
        justifyContent: "center",
        border: "none",
        borderRadius: "var(--radius-sm)",
        background: "var(--surface-sunken)",
        color: "var(--text-muted)",
        cursor: "pointer",
      }}
    >
      <icons.IconClose size={15} />
    </button>
  );

  const gif = (
    <div style={{ position: "relative", width: "100%", aspectRatio: "16 / 10", borderRadius: narrow ? 12 : 10, overflow: "hidden", background: "var(--surface-sunken)" }}>
      <img key={item.id} src={item.gif} alt={item.gifAlt} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
    </div>
  );

  const list = (
    <ol
      role="tablist"
      aria-label="Funktionen"
      style={{ listStyle: "none", margin: 0, padding: narrow ? "0 8px 12px" : "0 12px 12px", display: "flex", flexDirection: "column", gap: 2, ...(narrow ? {} : { flex: 1, overflowY: "auto" }) }}
    >
      {items.map((entryItem, k) => {
        const on = k === index;
        const done = k < index;
        return (
          <li key={entryItem.id}>
            <div
              role="tab"
              aria-selected={on}
              tabIndex={on ? -1 : 0}
              onClick={() => setIndex(k)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setIndex(k);
                }
              }}
              style={{
                display: "grid",
                gridTemplateColumns: `${narrow ? 28 : 24}px minmax(0,1fr)`,
                gap: narrow ? 12 : 10,
                padding: narrow ? "11px 8px" : "10px 8px",
                borderRadius: "var(--radius-md)",
                background: on ? "var(--surface-inset)" : "transparent",
                cursor: on ? "default" : "pointer",
              }}
            >
              <span
                style={{
                  width: narrow ? 28 : 24,
                  height: narrow ? 28 : 24,
                  borderRadius: "50%",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: "var(--font-mono)",
                  fontSize: 12,
                  fontWeight: 600,
                  background: on ? "var(--hm-green-500)" : done ? "var(--hm-green-50)" : "var(--surface-sunken)",
                  color: on ? "#fff" : done ? "var(--hm-green-700)" : "var(--text-muted)",
                }}
              >
                {done ? <icons.IconCheck size={13} /> : k + 1}
              </span>
              <span style={{ display: "flex", flexDirection: "column", gap: 4, paddingTop: 2 }}>
                <span style={{ fontSize: narrow ? 15 : 14, fontWeight: 650, lineHeight: 1.35, color: on ? "var(--text-strong)" : "var(--text-body)" }}>
                  {entryItem.title}
                </span>
                {on && <span style={{ fontSize: narrow ? 14 : 13, lineHeight: 1.5, color: "var(--text-body)" }}>{entryItem.text}</span>}
                {on && entryItem.action !== undefined && (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      finish(entryItem.action!.target);
                    }}
                    style={{
                      alignSelf: "flex-start",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      padding: 0,
                      minHeight: narrow ? 28 : undefined,
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      fontFamily: "var(--font-body)",
                      fontSize: narrow ? 14 : 13,
                      fontWeight: 600,
                      color: "var(--hm-green-700)",
                    }}
                  >
                    {entryItem.action.label} öffnen
                    <icons.IconArrowRight size={14} />
                  </button>
                )}
              </span>
            </div>
          </li>
        );
      })}
    </ol>
  );

  const footer = (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        gap: 10,
        padding: narrow ? "12px 16px 16px" : "14px 20px 18px",
        borderTop: "1px solid var(--border-subtle)",
        background: "var(--surface-card)",
      }}
    >
      {index > 0 && (
        <Button variant="secondary" onClick={() => setIndex(index - 1)} style={narrow ? { flex: 1 } : undefined}>
          Zurück
        </Button>
      )}
      <Button onClick={() => (last ? finish() : setIndex(index + 1))} style={narrow ? { flex: 1 } : undefined}>
        {last ? "Fertig" : "Weiter"}
      </Button>
    </div>
  );

  const frame: CSSProperties = narrow
    ? { width: "100%", maxWidth: "100%", height: "100%", maxHeight: "100%", margin: 0, marginTop: "auto", borderRadius: "16px 16px 0 0" }
    : { width: "min(100% - 2rem, 980px)", maxHeight: "min(100% - 2rem, 620px)", borderRadius: "var(--radius-xl)" };

  return (
    <dialog
      ref={ref}
      aria-labelledby="news-title"
      onCancel={(event) => {
        event.preventDefault();
        finish();
      }}
      style={{
        ...frame,
        padding: 0,
        border: "1px solid var(--border-subtle)",
        background: "var(--surface-card)",
        color: "var(--text-body)",
        boxShadow: "var(--shadow-lg)",
        overflow: "hidden",
      }}
    >
      {narrow ? (
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "14px 16px 10px" }}>
            {heading}
            {close}
          </div>
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            <div style={{ padding: "0 16px 12px" }}>{gif}</div>
            {list}
          </div>
          {footer}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1.45fr) minmax(0,1fr)", height: "min(100vh - 2rem, 600px)" }}>
          <div style={{ padding: 20, background: "var(--surface-inset)", display: "flex", alignItems: "center" }}>{gif}</div>
          <div style={{ display: "flex", flexDirection: "column", minHeight: 0, borderLeft: "1px solid var(--border-subtle)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "18px 20px 10px" }}>
              {heading}
              {close}
            </div>
            {list}
            {footer}
          </div>
        </div>
      )}
    </dialog>
  );
}
